import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { cartApi, orderApi, paymentApi, userApi } from '@/api/endpoints';
import { Alert, Loading } from '@/components/common/State';
import { AddressSearch } from '@/components/common/AddressSearch';
import { isAddressFilled } from '@/utils/postcode';
import { formatWon } from '@/utils/format';
import { DEV_PAYMENT } from '@/utils/devPayment';
import type { OrderCheckout } from '@/api/types';

interface CheckoutState {
  cartItemIds?: number[];
  lines?: { productId: number; optionId: number; quantity: number }[];
}

const emptyForm = {
  recipient: '',
  recipientPhone: '',
  zipcode: '',
  address1: '',
  address2: '',
  deliveryMemo: '',
};

export default function CheckoutPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const state = (useLocation().state ?? {}) as CheckoutState;
  const [form, setForm] = useState(emptyForm);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [saveAsDefault, setSaveAsDefault] = useState(true);
  const [saveFeedback, setSaveFeedback] = useState<string | null>(null);

  const addresses = useQuery({ queryKey: ['addresses'], queryFn: userApi.addresses });
  const cart = useQuery({
    queryKey: ['cart'],
    queryFn: cartApi.get,
    enabled: Boolean(state.cartItemIds?.length),
  });

  // 기본 배송지를 자동으로 채운다.
  useEffect(() => {
    const preset = addresses.data?.find((a) => a.isDefault) ?? addresses.data?.[0];
    if (preset) {
      setForm((prev) =>
        prev.recipient
          ? prev
          : {
              recipient: preset.recipient,
              recipientPhone: preset.phone,
              zipcode: preset.zipcode,
              address1: preset.address1,
              address2: preset.address2 ?? '',
              deliveryMemo: '',
            },
      );
    }
  }, [addresses.data]);

  const saveAddress = useMutation({
    mutationFn: () =>
      userApi.addAddress({
        recipient: form.recipient,
        phone: form.recipientPhone,
        zipcode: form.zipcode,
        address1: form.address1,
        address2: form.address2,
        isDefault: saveAsDefault,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['addresses'] });
      setSaveFeedback('배송지를 등록했습니다.');
    },
    onError: (e: Error) => setSaveFeedback(e.message),
  });

  // 입력한 배송지가 저장된 배송지 중에 없으면 새 배송지로 등록할 수 있게 한다.
  const savedAddresses = addresses.data ?? [];
  const isSavedAddress = savedAddresses.some(
    (a) =>
      a.recipient === form.recipient &&
      a.phone === form.recipientPhone &&
      a.zipcode === form.zipcode &&
      a.address1 === form.address1 &&
      (a.address2 ?? '') === form.address2,
  );
  const canSaveAddress =
    form.recipient.trim() !== '' && form.recipientPhone.trim() !== '' && isAddressFilled(form);
  // 첫 배송지는 서버가 항상 기본 배송지로 지정한다.
  const isFirstAddress = savedAddresses.length === 0;

  const selectedCartItems =
    cart.data?.items.filter((item) => state.cartItemIds?.includes(item.cartItemId)) ?? [];
  const itemTotal = selectedCartItems.reduce((sum, item) => sum + item.totalPrice, 0);
  const shippingFee = itemTotal === 0 || itemTotal >= 50000 ? 0 : 3000;

  const handleChange = (key: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm((prev) => ({ ...prev, [key]: e.target.value }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!isAddressFilled(form)) {
      setError('주소 찾기로 배송지 주소를 입력해 주세요.');
      return;
    }
    setSubmitting(true);
    let checkout: OrderCheckout | undefined;
    try {
      checkout = await orderApi.create({
        cartItemIds: state.cartItemIds,
        lines: state.lines,
        ...form,
      });
      // 첫 주문이면 서버가 입력한 배송지를 기본 배송지로 등록한다.
      queryClient.invalidateQueries({ queryKey: ['addresses'] });

      // 결제 성공 후 장바구니를 비우기 위해 주문한 항목을 잠시 보관한다.
      sessionStorage.setItem(
        'gency03.pendingOrder',
        JSON.stringify({ orderNo: checkout.orderNo, cartItemIds: state.cartItemIds ?? [] }),
      );

      const tossPayments = TossPayments(checkout.clientKey);
      const payment = tossPayments.payment({ customerKey: `user-${checkout.customerEmail}` });

      await payment.requestPayment({
        method: 'CARD',
        amount: { currency: 'KRW', value: checkout.amount },
        orderId: checkout.orderNo,
        orderName: checkout.orderName,
        customerEmail: checkout.customerEmail,
        customerName: checkout.customerName,
        successUrl: `${location.origin}/order/payment/success`,
        failUrl: `${location.origin}/order/payment/fail`,
        card: { useEscrow: false, flowMode: 'DEFAULT', useCardPoint: false, useAppCardOnly: false },
      });
    } catch (e) {
      // 결제창을 여는 단계에서 실패하면 successUrl/failUrl 어느 쪽으로도 가지 않아
      // 주문이 PENDING 으로 남는다. 개발 환경에서는 여기서 0원 결제로 넘겨 준다.
      if (DEV_PAYMENT && checkout) {
        try {
          await paymentApi.devApprove({
            orderNo: checkout.orderNo,
            cartItemIds: state.cartItemIds ?? [],
          });
          sessionStorage.removeItem('gency03.pendingOrder');
          queryClient.invalidateQueries({ queryKey: ['cart'] });
          queryClient.invalidateQueries({ queryKey: ['orders'] });
          navigate(`/order/payment/success?orderId=${checkout.orderNo}&amount=0&dev=1`, { replace: true });
          return;
        } catch {
          // 개발용 처리까지 실패하면 원래 오류를 그대로 보여 준다.
        }
      }
      setError(e instanceof Error ? e.message : '주문 처리 중 오류가 발생했습니다.');
      setSubmitting(false);
    }
  };

  if (!state.cartItemIds?.length && !state.lines?.length) {
    return (
      <Alert tone="error">
        주문할 상품이 없습니다.{' '}
        <button type="button" className="btn btn--ghost btn--sm" onClick={() => navigate('/cart')}>
          장바구니로 이동
        </button>
      </Alert>
    );
  }

  if (cart.isLoading || addresses.isLoading) return <Loading />;

  return (
    <>
      <h1 className="panel-title">주문 / 결제</h1>

      <form className="cart-layout" onSubmit={handleSubmit}>
        <div className="card">
          <div className="card__body">
            <h2 style={{ fontSize: 17, marginTop: 0 }}>배송 정보</h2>

            {addresses.data && addresses.data.length > 0 && (
              <div className="field">
                <label htmlFor="saved">저장된 배송지</label>
                <select
                  id="saved"
                  className="select"
                  onChange={(e) => {
                    const found = addresses.data?.find((a) => a.addressId === Number(e.target.value));
                    if (found) {
                      setForm({
                        recipient: found.recipient,
                        recipientPhone: found.phone,
                        zipcode: found.zipcode,
                        address1: found.address1,
                        address2: found.address2 ?? '',
                        deliveryMemo: form.deliveryMemo,
                      });
                    }
                  }}
                >
                  <option value="">직접 입력</option>
                  {addresses.data.map((address) => (
                    <option key={address.addressId} value={address.addressId}>
                      {address.alias ?? '배송지'} · {address.recipient} ({address.address1})
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div className="field">
              <label htmlFor="recipient">받는 분 *</label>
              <input id="recipient" className="input" required value={form.recipient} onChange={handleChange('recipient')} />
            </div>
            <div className="field">
              <label htmlFor="phone">연락처 *</label>
              <input
                id="phone"
                className="input"
                required
                placeholder="010-0000-0000"
                value={form.recipientPhone}
                onChange={handleChange('recipientPhone')}
              />
            </div>
            <AddressSearch
              required
              zipcode={form.zipcode}
              address1={form.address1}
              detailInputId="address2"
              onSelect={(picked) => setForm((prev) => ({ ...prev, ...picked, address2: '' }))}
            />
            <div className="field">
              <label htmlFor="address2">상세 주소</label>
              <input id="address2" className="input" value={form.address2} onChange={handleChange('address2')} />
            </div>
            <div className="field">
              <label htmlFor="memo">배송 메모</label>
              <input id="memo" className="input" value={form.deliveryMemo} onChange={handleChange('deliveryMemo')} />
            </div>

            {!isSavedAddress && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                <button
                  type="button"
                  className="btn btn--outline btn--sm"
                  disabled={!canSaveAddress || saveAddress.isPending}
                  onClick={() => {
                    setSaveFeedback(null);
                    saveAddress.mutate();
                  }}
                >
                  {saveAddress.isPending ? '등록 중…' : '배송지 등록'}
                </button>
                <label style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 14 }}>
                  <input
                    type="checkbox"
                    checked={isFirstAddress || saveAsDefault}
                    disabled={isFirstAddress}
                    onChange={(e) => setSaveAsDefault(e.target.checked)}
                  />
                  기본 배송지로
                </label>
                {isFirstAddress && (
                  <span className="muted" style={{ fontSize: 12 }}>
                    첫 배송지는 기본 배송지로 등록됩니다.
                  </span>
                )}
              </div>
            )}
            {saveFeedback && (
              <p className="muted" style={{ fontSize: 13, margin: '8px 0 0' }}>
                {saveFeedback}
              </p>
            )}
          </div>
        </div>

        <div className="card summary-box">
          <div className="card__body">
            <h2 style={{ fontSize: 17, marginTop: 0 }}>주문 상품</h2>
            {selectedCartItems.map((item) => (
              <div className="summary-row" key={item.cartItemId}>
                <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {item.productName} × {item.quantity}
                </span>
                <span>{formatWon(item.totalPrice)}</span>
              </div>
            ))}
            {state.lines?.map((line) => (
              <div className="summary-row" key={line.optionId}>
                <span>바로구매 상품 × {line.quantity}</span>
                <span className="muted">주문서에서 계산</span>
              </div>
            ))}

            {selectedCartItems.length > 0 && (
              <>
                <div className="summary-row">
                  <span>배송비</span>
                  <span>{shippingFee === 0 ? '무료' : formatWon(shippingFee)}</span>
                </div>
                <div className="summary-row summary-row--total">
                  <span>결제 금액</span>
                  <span>{formatWon(itemTotal + shippingFee)}</span>
                </div>
              </>
            )}

            {error && <Alert tone="error">{error}</Alert>}

            <button type="submit" className="btn btn--primary btn--lg btn--block" disabled={submitting}>
              {submitting ? '결제창을 여는 중…' : '결제하기'}
            </button>
            <p className="muted" style={{ fontSize: 12, marginTop: 10 }}>
              토스페이먼츠 테스트 결제창이 열립니다.
            </p>
          </div>
        </div>
      </form>
    </>
  );
}
